import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import Keycloak from 'keycloak-js';
import { environment } from '../environments/environment';

/**
 * The hub's login, on keycloak-js.
 *
 * keycloak-js sends a `state` with every login and only exchanges a code that comes back with a
 * state it issued, so a `?code=` planted in a link is ignored. It also does PKCE (S256), so an
 * intercepted code is useless without the verifier that never leaves this tab.
 *
 * Tokens live in memory only. A reload or a new tab gets fresh ones by bouncing through Keycloak,
 * which returns straight away while the SSO session is alive, so nothing that grants API access is
 * ever written to localStorage.
 */
@Injectable({
  providedIn: 'root'
})
export class KeycloakService {

  /** Refresh the access token when it has less than this many seconds left. */
  private static readonly MIN_VALIDITY_SECONDS = 30;

  /** Where the previous hand-rolled flow kept its tokens. */
  private static readonly LEGACY_TOKEN_KEYS = ['access_token', 'refresh_token'];

  /** The page to reopen once the round trip through Keycloak comes back to the origin. */
  private static readonly RETURN_PATH_KEY = 'hub.returnPath';

  private readonly keycloak = new Keycloak({
    url: environment.keycloak.authServerUrl,
    realm: environment.keycloak.realm,
    clientId: environment.keycloak.clientId
  });

  /**
   * Back to wherever the hub is running. Taken from the page rather than the environment file, so
   * a local build pointed at the dev API (`npm run start:dev`) comes back to localhost after login
   * instead of to the deployed dev hub. Keycloak's dl-hub client must list the origin as a valid
   * redirect URI, which it does for localhost:11000 and the deployed hubs.
   */
  private get redirectUri(): string {
    return this.document.location.origin;
  }

  constructor(@Inject(DOCUMENT) private document: Document) {
  }

  /**
   * Resolves once there is a logged-in user. Without one it sends the page to the Keycloak login
   * and never resolves, so the app does not bootstrap unauthenticated.
   */
  public async init(): Promise<void> {
    this.clearLegacyTokens();
    if (!this.isLoginCallback()) {
      this.rememberReturnPath();
    }

    await this.keycloak.init({
      onLoad: 'login-required',
      redirectUri: this.redirectUri,
      pkceMethod: 'S256',
      // The session-status iframe needs third-party cookies, which Safari and others block.
      // Expiry is handled by refreshing on demand in getToken() instead.
      checkLoginIframe: false
    });

    // Runs before the router's first navigation, which then starts from the restored URL.
    this.restoreReturnPath();
  }

  /**
   * A bearer token good for at least MIN_VALIDITY_SECONDS, refreshed first when it is not.
   * Concurrent callers share one refresh.
   */
  public async getToken(): Promise<string> {
    try {
      await this.keycloak.updateToken(KeycloakService.MIN_VALIDITY_SECONDS);
    } catch (err) {
      // The refresh token expired or the SSO session ended; only a new login can get a token.
      console.warn('Token refresh failed, redirecting to login', err);
      this.rememberReturnPath();
      await this.keycloak.login({ redirectUri: this.redirectUri });
    }
    return this.keycloak.token as string;
  }

  public logout(): void {
    this.keycloak.logout({ redirectUri: this.redirectUri });
  }

  /** ADMIN as a realm role or as a role on the hub's own client. */
  public hasAdminRole(): boolean {
    return this.keycloak.hasRealmRole('ADMIN') || this.keycloak.hasResourceRole('ADMIN');
  }

  /**
   * Keycloak sends the browser back to the bare origin, since that is the redirect URI its client
   * is known to accept, and with tokens in memory every reload makes that trip. So the page the
   * user was on is kept in sessionStorage (this tab only) and put back once login completes.
   */
  private rememberReturnPath(): void {
    const { pathname, search, hash } = this.document.location;
    try {
      sessionStorage.setItem(KeycloakService.RETURN_PATH_KEY, pathname + search + hash);
    } catch {
      // Storage disabled: the user lands on the home page after login, as before.
    }
  }

  private restoreReturnPath(): void {
    let path: string | null = null;
    try {
      path = sessionStorage.getItem(KeycloakService.RETURN_PATH_KEY);
      sessionStorage.removeItem(KeycloakService.RETURN_PATH_KEY);
    } catch {
      return;
    }
    // Only a same-origin path; `//host` would be protocol-relative.
    if (!path || !path.startsWith('/') || path.startsWith('//')) {
      return;
    }
    const { pathname, search, hash } = this.document.location;
    if (path !== pathname + search + hash) {
      try {
        this.document.defaultView?.history.replaceState(null, '', path);
      } catch {
        // A value replaceState refuses must not stop the app from starting; land on home instead.
      }
    }
  }

  /** The page load that carries Keycloak's answer back (`#state=…&code=…`) rather than a user URL. */
  private isLoginCallback(): boolean {
    return new URLSearchParams(this.document.location.hash.substring(1)).has('state');
  }

  /**
   * Browsers that used the hub before this change still hold its tokens. The refresh token outlives
   * the access token by hours, so do not leave it lying around.
   */
  private clearLegacyTokens(): void {
    try {
      KeycloakService.LEGACY_TOKEN_KEYS.forEach(key => localStorage.removeItem(key));
    } catch {
      // Storage disabled: then there is nothing stored to clear either.
    }
  }
}
